import BN from "bn.js";

import { fixExponential } from "../utils/helpers";

const ETHval = new BN('1000000000000000000');	// 10**18
const zeroBN = new BN('0');

/**
 * Returns BN from human-readable string or number (float possible) with passed decimals.
 *
 * Default decimals: 18;
 */
const valToBN = (value: string | number, decimals?: number): BN => {
	if(
		Number.isNaN(+value) ||
		!Number.isFinite(+value) ||
		(typeof decimals === 'number' && (
			Number.isNaN(decimals) ||
			!Number.isFinite(decimals)
		))
	) throw new Error('Error code: 6_15');

	const dec = typeof decimals === 'number' ? decimals : 18;
	const arr = value.toString().split('.');

	if (dec === 0) return new BN(arr[0]);
	if (arr.length === 1) return new BN(arr[0] + '0'.repeat(dec));
	return new BN(arr[0] + arr[1].slice(0, dec).padEnd(dec, '0'));
}

/**
 * Returns a human-readable string of stringBN with passed decimals.
 *
 * Default decimals: 18.
 */
const pseudoBNToVal = (value: string | number, decimals?: number): string => {
	if(Number.isNaN(value)) throw new Error('Error code: 6_16');

	const dec = typeof decimals === 'number'? decimals : 18;
	if (value === '0') return '0';
	let str = value.toString();
	let res = '';
	if (str[0] === '-') {
		res = '-';
		str = str.substring(1);
	}
	str = str.replace(/^0+/, '');
	if (str.length > dec) {
		res += str.substring(0, str.length - dec) + '.' + str.substring(str.length - dec);
	}
	else {
		res += '0.' + str.padStart(dec, '0');
	}
	res = res.replace(/0+$/, '');
	if (res[res.length - 1] === '.') return res.substring(0, res.length-1);
	// if (res[res.length - 1] === '.') return res + '0';
	return res;
}

interface iConstructorOptions {
	/**
	 * Decimals which will be used for construction.
	 *
	 * Default: 0.
	 */
	currentDecimals?: number,
	/**
	 * Decimals which will be used for `getDecBN`.
	 *
	 * Default: 18.
	 */
	decimals?: number,
	/**
	 * Decimals wich will be used for `valueStr`.
	 *
	 * Default: unlimited.
	 */
	// stringDecimals?: number,
}

interface iNewDecimalOption {
	/**
	 * New BigNumber will have this decimals.
	 */
	newDecimals?: number,
}
interface iRaiseValueOption{
	/**
	 * Raises value to power of 18.
	 *
	 * Default: false.
	 */
	raiseValue?: boolean,
}


export class cBigNumber {
	/**
	 * Value in BN with 18 decimals.
	 */
	valueBN18: BN;
	/**
	 * BN decimals (default 18).
	 */
	decimals: number;
	/**
	 * Human-readable string (with decimal part).
	 */
	valueStr: string;

	constructor (value: string | number | BN,
		/**
		 * Additional options (`currentDecimals`, `decimals`).
		 */
		options?: iConstructorOptions,
	){
		if (options && (
			(typeof options.decimals === 'number' && (!Number.isSafeInteger(options.decimals) || options.decimals > 255 || options.decimals < 0 )) ||
			(typeof options.currentDecimals === 'number' && (!Number.isSafeInteger(options.currentDecimals) || options.currentDecimals > 255 || options.currentDecimals < 0 ))
		)){
			throw new Error();
		}
		this.decimals = (options && typeof options.decimals === 'number')? options.decimals : 18;
		const currentDecimals: number = (options && typeof options.currentDecimals === 'number')? options.currentDecimals : 0;

		if (value instanceof BN){
			this.valueBN18 = value.mul( new BN(10).pow(new BN(18 - currentDecimals)) );
			this.valueStr = pseudoBNToVal( this.valueBN18.toString() );
			return;
		}

		if (typeof value === 'number'){
			if (Number.isNaN(value) || !Number.isFinite(value)){
				throw new Error();
			}
		}

		this.valueBN18 = valToBN(fixExponential(value), 18 - currentDecimals);
		this.valueStr = pseudoBNToVal( this.valueBN18.toString() );
	}

	/**
	 * @returns BN with assigned decimals.
	 */
	getDecBN(): BN {
		return this.valueBN18.div( new BN(10).pow(new BN(18 - this.decimals)) );
	}

	/**
	 * Multiplies two values in BN with 18 decimals.
	 * @param value Multiplicator.
	 * @param options Additional options (`newDecimals`, `raiseValue`).
	 */
	mul(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
	// mul(value: string | number | BN | cBigNumber, options?: iOperationOptions): cBigNumber {
		const mult: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		const res = this.valueBN18.mul(mult).div(ETHval);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	}

	/**
	 * Divides two values in BN with 18 decimals.
	 * @param value Divider.
	 * @param options Additional options (`newDecimals`, `raiseValue`).
	 */
	div(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
	// div(value: string | number | BN | cBigNumber, options?: iOperationOptions): cBigNumber {
		const divider: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		if (divider.isZero()) throw new Error('Division by zero');

		const res = this.valueBN18.mul(ETHval).div(divider);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	}

	/**
	 * Adds one value to another in BN with 18 decimals.
	 * @param value Value to add.
	 * @param options Additional options (`newDecimals`, `raiseValue`).
	 */
	add(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
	// add(value: string | number | BN | cBigNumber, options?: iOperationOptions): cBigNumber {
		const additional: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		const res = this.valueBN18.add(additional);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	}

	/**
	 * Subtracts value from another in BN with 18 decimals.
	 * @param value Subtrahend.
	 * @param options Additional options (`newDecimals`, `raiseValue`).
	 */
	sub(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
	// sub(value: string | number | BN | cBigNumber, options?: iOperationOptions): cBigNumber {
		const subtrahend: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		const res = this.valueBN18.sub(subtrahend);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	}

	/**
	 * Absolute value.
	 */
	abs(): cBigNumber {
		return this.isNeg()? this.neg() : this;
	}

	/**
	 * a less than b.
	 */
	lt(value: string | number | BN | cBigNumber, options?: iRaiseValueOption): boolean {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		return this.valueBN18.lt(b);
	}

	/**
	 * a less than or equals b.
	 */
	lte(value: string | number | BN | cBigNumber, options?: iRaiseValueOption): boolean {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		return this.valueBN18.lte(b);
	}

	/**
	 * a greater than b.
	 */
	gt(value: string | number | BN | cBigNumber, options?: iRaiseValueOption): boolean {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		return this.valueBN18.gt(b);
	}

	/**
	 * a greater than or equals b.
	 */
	gte(value: string | number | BN | cBigNumber, options?: iRaiseValueOption): boolean {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		return this.valueBN18.gte(b);
	}

	/**
	 * a equals b.
	 */
	eq(value: string | number | BN | cBigNumber, options?: iRaiseValueOption): boolean {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		return this.valueBN18.eq(b);
	}

	/**
	 * Remainder from division (modulo).
	 */
	mod(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;

		const res = this.valueBN18.mod(b);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	}

	/**
	 * Check if value is negative.
	 */
	isNeg(): boolean {
		return this.valueBN18.isNeg();
	}

	/**
	 * Check if value is zero.
	 */
	isZero(): boolean {
		return this.valueBN18.isZero();
	}

	/**
	 * Check if value is greater than zero.
	 */
	gtZero(): boolean {
		return this.valueBN18.gt(zeroBN);
	}

	/**
	 * Check if value is even.
	 */
	isEven(): boolean {
		return this.valueBN18.isEven();
	}

	/**
	 * Check if value is odd.
	 */
	isOdd(): boolean {
		return this.valueBN18.isOdd();
	}

	/**
	 * Negate sign.
	 */
	neg(): cBigNumber {
		return this.mul(-1, {raiseValue: true});
	}

	/**
	 * Raise a to the power of b.
	 */
	/* pow(value: string | number | BN | cBigNumber, options?: iNewDecimalOption & iRaiseValueOption): cBigNumber {
		const b: BN = (typeof value === 'number' || typeof value === 'string')
			? options?.raiseValue
				? valToBN(value)
				: new BN(value)
			: (value instanceof BN)
				? options?.raiseValue
					? value.mul(ETHval)
					: value
				: options?.raiseValue
					? value.valueBN18.mul(ETHval)
					: value.valueBN18
		;
		// new cBigNumber(3).pow(2, {raiseValue: true}) = 3000000000000000000 ** 2000000000000000000 - bad idea
		// new cBigNumber(3).pow(2) = 3000000000000000000 ** 2 - normal
		// new cBigNumber(4).pow(0.5) = 4000000000000000000 ** 0.5 - not working

		const res = this.valueBN18.pow(b);
		return new cBigNumber(res, {currentDecimals: 18, decimals: options?.newDecimals});
	} */

	/**
	 *  Returns the maximum of cBigNumber instances.
	 */
	static max(values: cBigNumber[]): cBigNumber {
		if (values.length === 0) throw new Error();
		return values.reduce((previousValue, currentValue) => currentValue.gt(previousValue)? currentValue : previousValue);
	}

	/**
	 *  Returns the minimum of cBigNumber instances.
	 */
	static min(values: cBigNumber[]): cBigNumber {
		if (values.length === 0) throw new Error();
		return values.reduce((previousValue, currentValue) => currentValue.lt(previousValue)? currentValue : previousValue);
	}
}